import React, { useState, useEffect } from 'react';

import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Icon from '../../../components/AppIcon';
import repositoryContainer from '../../../services/RepositoryContainer';
import { useToast } from '../../../components/context/ToastContext';
import { useAuth } from '../../../components/context/AuthContext';
import { parseImages, serializeImages } from '../../../utils/imageUtils';

const getInitialFormData = (service = null) => ({
  name: service?.name || '',
  description: service?.description || '',
  price: service?.price != null ? service.price.toString() : '',
  duration:
    service?.durationInMinutes != null
      ? service.durationInMinutes.toString()
      : service?.duration != null
        ? service.duration.toString()
        : '',
  categoryId: service?.categoryId || '',
  images: parseImages(service?.imageUrl || service?.image || ''),
  isPopular: Boolean(service?.isPopular),
  type: String(service?.type || 'service').toLowerCase(),
  pricingMode: String(service?.type || 'service').toLowerCase() === 'service' && (
    String(service?.pricingMode || '').toLowerCase() === 'range'
    || (service?.minimumPrice != null && service?.maximumPrice != null)
  ) ? 'range' : 'fixed',
  maximumPrice: service?.maximumPrice != null ? service.maximumPrice.toString() : '',
  initialStock: service ? '' : '',
  lowStockThreshold: service?.lowStockThreshold != null ? service.lowStockThreshold.toString() : ''
});

const ServiceModal = ({ isOpen, onClose, onSave, service, categories }) => {
  const [formData, setFormData] = useState(getInitialFormData());
  const [restockData, setRestockData] = useState({ quantity: '', reason: '' });
  const [errors, setErrors] = useState({});
  const { addToast } = useToast();
  const [uploadingSlot, setUploadingSlot] = useState(null);
  const { isProOrBusiness } = useAuth();

  useEffect(() => {
    setFormData(getInitialFormData(service));
    setRestockData({ quantity: '', reason: '' });
    setErrors({});
  }, [service, isOpen]);

  const handleRestockChange = (field, value) => {
    setRestockData(prev => ({ ...prev, [field]: value }));
  };

  const handleRestock = async () => {
    if (!restockData.quantity || parseInt(restockData.quantity) <= 0) {
      setErrors(prev => ({ ...prev, restockQuantity: 'Valid quantity required' }));
      return;
    }

    try {
      await repositoryContainer.stock.restock(service.id, {
        quantity: parseInt(restockData.quantity),
        reason: restockData.reason || 'Manual restock'
      });
      
      // Reset restock form
      setRestockData({ quantity: '', reason: '' });
      // Maybe refresh the service data or show success message
      // alert('Stock restocked successfully!');
      addToast('Stock restocked successfully!', 'success');
    } catch (error) {
      console.error('Failed to restock:', error);
      // alert('Failed to restock. Please try again.');
      addToast('Failed to restock. Please try again.', 'error');
    }
  };

  const handleChange = (field, value) => {
    // if type changes and it's not a service, clear duration
    if (field === 'type' && value !== 'service') {
      setFormData(prev => ({ ...prev, [field]: value, duration: '', pricingMode: 'fixed', maximumPrice: '' }));
      setErrors(prev => ({ ...prev, duration: '', maximumPrice: '' }));
    } else if (field === 'type' && value === 'service') {
      // if changing to service, clear stock fields
      setFormData(prev => ({ ...prev, [field]: value, initialStock: '', lowStockThreshold: '' }));
      setErrors(prev => ({ ...prev, initialStock: '', lowStockThreshold: '' }));
    } else {
      setFormData(prev => ({ ...prev, [field]: value }));
    }

    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleSlotUpload = async (slot, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingSlot(slot);
    try {
      const result = await repositoryContainer.file.upload(file, 'products');
      const uploadedUrl = result?.url || result?.path || result;
      if (uploadedUrl) {
        setFormData(prev => ({ ...prev, images: { ...prev.images, [slot]: uploadedUrl } }));
      }
    } catch {
      addToast('Image upload failed. Please try again.', 'error');
    } finally {
      setUploadingSlot(null);
    }
  };

  const removeImage = (slot) => {
    setFormData(prev => {
      const remaining = ['1', '2', '3']
        .filter(k => k !== slot && prev.images[k])
        .map(k => prev.images[k]);
      const compacted = {};
      remaining.forEach((url, i) => { compacted[String(i + 1)] = url; });
      return { ...prev, images: compacted };
    });
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData?.name?.trim()) {
      newErrors.name = 'Service name is required';
    }
    
    // if (!formData?.description?.trim()) {
    //   newErrors.description = 'Description is required';
    // }
    
    if (!formData?.price || parseFloat(formData?.price) <= 0) {
      newErrors.price = 'Valid price is required';
    }

    if (formData.type === 'service' && formData.pricingMode === 'range') {
      const minimumPrice = Number(formData.price);
      const maximumPrice = Number(formData.maximumPrice);
      if (!Number.isFinite(maximumPrice) || maximumPrice <= 0) {
        newErrors.maximumPrice = 'Valid maximum price is required';
      } else if (maximumPrice <= minimumPrice) {
        newErrors.maximumPrice = 'Maximum price must be greater than the minimum price';
      }
    }

    if (!formData?.type) {
      newErrors.type = 'Type is required';
    }

    // duration is only required when it's a service
    if (formData.type === 'service') {
      if (!formData?.duration || parseInt(formData?.duration) <= 0) {
        newErrors.duration = 'Valid duration is required';
      }
    }

    // stock fields are only required when creating a new product
    if (formData.type === 'product' && !service) {
      if (!formData?.initialStock || parseInt(formData?.initialStock) < 0) {
        newErrors.initialStock = 'Initial stock is required for new products';
      }
    }
    
    if (!formData?.categoryId) {
      newErrors.category = 'Category is required';
    }
    
    // if (!formData?.image?.trim()) {
    //   newErrors.image = 'Image URL is required';
    // }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    
    if (validateForm()) {
      const serviceData = {
        ...formData,
        price: parseFloat(formData?.price),
        pricingMode: formData.type === 'service' ? formData.pricingMode : 'fixed',
        minimumPrice: formData.type === 'service' && formData.pricingMode === 'range'
          ? parseFloat(formData.price)
          : undefined,
        maximumPrice: formData.type === 'service' && formData.pricingMode === 'range'
          ? parseFloat(formData.maximumPrice)
          : undefined,
        duration: formData.type === 'service' ? parseInt(formData?.duration) : undefined,
        initialStock: (formData.type === 'product' && !service) ? parseInt(formData?.initialStock) : undefined,
        lowStockThreshold: formData.type === 'product' && formData?.lowStockThreshold ? parseInt(formData?.lowStockThreshold) : undefined,
        id: service?.id || Date.now(),
        imageAlt: `${formData?.name} image`,
        image: serializeImages(formData.images),
      };
      delete serviceData.images;
      
      onSave(serviceData);
      onClose();
    }
  };

  const types = [
    { name: 'Service', value: 'service' },
    { name: 'Product', value: 'product' },   
  ];

  if (!isOpen) return null;

  const categoryOptions = categories?.map(cat => ({
    value: cat?.id,
    label: cat?.name
  }));
  
  const typeOptions = types?.map(type => ({
    value: type?.value,
    label: type?.name
  }));
  const isRangePricing = formData.type === 'service' && formData.pricingMode === 'range';

  const togglePricingMode = () => {
    if (formData.type !== 'service') return;
    const nextMode = isRangePricing ? 'fixed' : 'range';
    setFormData((current) => ({
      ...current,
      pricingMode: nextMode,
      maximumPrice: nextMode === 'fixed' ? '' : current.maximumPrice,
    }));
    setErrors((current) => ({ ...current, price: '', maximumPrice: '' }));
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-background/80 backdrop-blur-sm">
      <div className="bg-card border border-border rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-4 md:p-6 border-b border-border">
          <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground">
            {service
              ? `Edit ${formData.type === 'product' ? 'Product' : 'Service'}`
              : 'Add New Item'}
          </h2>
          <Button
            variant="ghost"
            size="icon"
            iconName="X"
            iconSize={20}
            onClick={onClose}
          />
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto custom-scrollbar p-4 md:p-6">
          <div className="space-y-4 md:space-y-5">
            <Input
              label="Name"
              type="text"
              placeholder={formData.type === 'product' ? 'e.g., Nail Polish' : formData.type === 'service' ? 'e.g., Classic Manicure' : 'Enter a name...'}
              value={formData?.name}
              onChange={(e) => handleChange('name', e?.target?.value)}
              error={errors?.name}
              required
            />

            <div className="space-y-2">
              <label className="block text-sm font-caption font-medium text-foreground">
                Description 
              </label>
              <textarea
                value={formData?.description}
                onChange={(e) => handleChange('description', e?.target?.value)}
                placeholder="Describe the item in detail..."
                rows={4}
                className={`w-full px-4 py-3 bg-background border rounded-lg font-caption text-sm md:text-base text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary transition-all duration-250 ${
                  errors?.description ? 'border-error' : 'border-input'
                }`}
              />
              {errors?.description && (
                <p className="text-xs text-error font-caption mt-1">{errors?.description}</p>
              )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {formData.type === 'service' ? (
              <div className="space-y-2">
                <label htmlFor="service-price" className={`text-sm font-medium leading-none ${errors?.price ? 'text-destructive' : 'text-foreground'}`}>
                  {isRangePricing ? 'Minimum price (GHS)' : 'Price (GHS)'}
                  <span className="ml-1 text-destructive">*</span>
                </label>
                <div className={`grid h-10 grid-cols-2 overflow-hidden rounded-md border bg-background focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2 ${errors?.price ? 'border-destructive' : 'border-input'}`}>
                  <input
                    id="service-price"
                    type="number"
                    placeholder="0.00"
                    value={formData?.price}
                    onChange={(event) => handleChange('price', event.target.value)}
                    required
                    min="0.01"
                    step="0.01"
                    className="min-w-0 border-0 bg-transparent px-3 py-2 text-sm text-foreground outline-none placeholder:text-muted-foreground focus:ring-0"
                  />
                  <button
                    type="button"
                    onClick={togglePricingMode}
                    aria-pressed={isRangePricing}
                    className={`flex min-w-0 items-center justify-center gap-1.5 border-l border-input px-2 text-xs font-medium transition-colors font-caption ${
                      isRangePricing
                          ? 'bg-primary/10 text-primary hover:bg-primary/15'
                          : 'bg-muted/30 text-foreground hover:bg-muted'
                    }`}
                  >
                    <Icon name="SlidersHorizontal" size={14} />
                    <span className="truncate">{isRangePricing ? 'Use fixed price' : 'Use price range'}</span>
                  </button>
                </div>
                {errors?.price && <p className="text-sm text-destructive">{errors.price}</p>}
                {isRangePricing && (
                  <Input
                    id="service-maximum-price"
                    label="Maximum price (GHS)"
                    type="number"
                    placeholder="0.00"
                    value={formData.maximumPrice}
                    onChange={(event) => handleChange('maximumPrice', event.target.value)}
                    error={errors.maximumPrice}
                    required
                    min="0.01"
                    step="0.01"
                    description="Customers will see this range until you confirm the final price."
                  />
                )}
              </div>
              ) : (
                <Input
                  label="Price (GHS)"
                  type="number"
                  placeholder="0.00"
                  value={formData.price}
                  onChange={(event) => handleChange('price', event.target.value)}
                  error={errors.price}
                  required
                  min="0.01"
                  step="0.01"
                />
              )}

              <Select
                label="Type"
                placeholder="Select a type"
                options={typeOptions}
                value={formData?.type}
                onChange={(value) => handleChange('type', value)}
                error={errors?.type}
                required
                disabled={!!service} // Disable type selection when editing existing items
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Select
                label="Category"
                placeholder="Select a category"
                options={categoryOptions}
                value={formData?.categoryId}
                onChange={(value) => handleChange('categoryId', value)}
                error={errors?.category}
                required
              />

              {formData.type === 'service' && (
                <Input
                  label="Duration (minutes)"
                  type="number"
                  placeholder="0"
                  value={formData?.duration}
                  onChange={(e) => handleChange('duration', e?.target?.value)}
                  error={errors?.duration}
                  min="1"
                />
              )}

              {/* Product fields - different for create vs edit */}
              {formData.type === 'product' && !service && (
                <>
                  <Input
                    label="Initial Stock"
                    type="number"
                    placeholder="0"
                    value={formData?.initialStock}
                    onChange={(e) => handleChange('initialStock', e?.target?.value)}
                    error={errors?.initialStock}
                    min="0"
                    required
                  />
                  <Input
                    label="Low Stock Threshold"
                    type="number"
                    placeholder="5"
                    value={formData?.lowStockThreshold}
                    onChange={(e) => handleChange('lowStockThreshold', e?.target?.value)}
                    error={errors?.lowStockThreshold}
                    min="0"
                  />
                </>
              )}

              {/* Edit existing product */}
              {formData.type === 'product' && service && (
                <>
                  <div className="sm:col-span-2">
                    <div className="p-4 bg-muted/50 rounded-lg">
                      <h4 className="font-medium text-sm text-foreground mb-2">Current Stock</h4>
                      <div className="flex items-center gap-4">
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-muted-foreground">Quantity:</span>
                          <span className="font-medium">{service?.currentStock || 0}</span>
                        </div>
                        {service?.isLowStock && (
                          <span className="px-2 py-1 bg-warning/10 text-warning text-xs rounded-full">
                            Low Stock
                          </span>
                        )}
                        {service?.isOutOfStock && (
                          <span className="px-2 py-1 bg-destructive/10 text-destructive text-xs rounded-full">
                            Out of Stock
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <Input
                    label="Low Stock Threshold"
                    type="number"
                    placeholder="5"
                    value={formData?.lowStockThreshold}
                    onChange={(e) => handleChange('lowStockThreshold', e?.target?.value)}
                    error={errors?.lowStockThreshold}
                    min="0"
                  />
                </>
              )}
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <label className="block text-sm font-caption font-medium text-foreground">
                  {formData.type === 'product' ? 'Product Images' : 'Service Images'}
                </label>
              </div>
              {!isProOrBusiness && (
                <p className="text-xs text-muted-foreground font-caption">
                  Multiple images are available on Pro and Business plans. Starter users may upload only the primary image.
                </p>
              )}

              <div className="grid grid-cols-3 gap-3">
                {['1', '2', '3'].map((slot) => {
                  const imgUrl = formData.images[slot];
                  const isSlotUploading = uploadingSlot === slot;
                  const isPrimary = slot === '1';
                  const slotAllowed = isProOrBusiness || isPrimary;
                  const isDisabled = !slotAllowed || (!imgUrl && !isPrimary && !formData.images[String(Number(slot) - 1)]);

                  return (
                    <div
                      key={slot}
                      className={`relative aspect-square rounded-xl overflow-hidden border-2 border-dashed transition-colors ${
                        imgUrl ? 'border-primary/40' : isDisabled ? 'border-border opacity-40' : 'border-border hover:border-primary/50'
                      } bg-muted/30`}
                    >
                      {imgUrl ? (
                        <>
                          <img src={imgUrl} alt={`Image ${slot}`} className="w-full h-full object-cover" />
                          {isPrimary && (
                            <div className="absolute bottom-0 left-0 right-0 bg-primary/80 backdrop-blur-sm text-primary-foreground text-[10px] font-bold text-center py-0.5">
                              Primary
                            </div>
                          )}
                          {slotAllowed && (
                            <button
                              type="button"
                              onClick={() => removeImage(slot)}
                              className="absolute top-1.5 right-1.5 w-6 h-6 bg-black/60 hover:bg-black/80 text-white rounded-full flex items-center justify-center transition-colors"
                            >
                              <Icon name="X" size={11} />
                            </button>
                          )}
                          {!slotAllowed && (
                            <div className="absolute inset-x-0 bottom-0 rounded-b-xl bg-black/60 text-[10px] text-white text-center py-1">
                              Pro + Business only
                            </div>
                          )}
                        </>
                      ) : (
                        <label className={`w-full h-full flex flex-col items-center justify-center gap-1 ${isDisabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}>
                          {isSlotUploading ? (
                            <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                          ) : (
                            <>
                              <Icon name="Plus" size={20} color="var(--color-muted-foreground)" />
                              {isPrimary && (
                                <span className="text-[10px] text-muted-foreground font-caption">Primary</span>
                              )}
                              {!slotAllowed && (
                                <span className="text-[10px] text-muted-foreground font-caption">Pro + Business only</span>
                              )}
                            </>
                          )}
                          <input
                            type="file"
                            className="hidden"
                            accept="image/*"
                            onChange={(e) => handleSlotUpload(slot, e)}
                            disabled={!!uploadingSlot || isDisabled}
                          />
                        </label>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Restock section for editing products */}
            {formData.type === 'product' && service && (
              <div className="p-4 bg-primary/5 border border-primary/20 rounded-lg">
                <h4 className="font-medium text-sm text-foreground mb-3">Restock Product</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Input
                    label="Quantity to Add"
                    type="number"
                    placeholder="0"
                    value={restockData?.quantity}
                    onChange={(e) => handleRestockChange('quantity', e?.target?.value)}
                    error={errors?.restockQuantity}
                    min="1"
                  />
                  <Input
                    label="Reason (Optional)"
                    type="text"
                    placeholder="e.g., Weekly delivery"
                    value={restockData?.reason}
                    onChange={(e) => handleRestockChange('reason', e?.target?.value)}
                  />
                </div>
                <div className="mt-3">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleRestock}
                    disabled={!restockData?.quantity || parseInt(restockData?.quantity) <= 0}
                    iconName="Plus"
                    iconPosition="left"
                  >
                    Add Stock
                  </Button>
                </div>
              </div>
            )}

            <div className="flex items-center gap-3 p-4 bg-muted/50 rounded-lg">
              <input
                type="checkbox"
                id="isPopular"
                checked={formData?.isPopular}
                onChange={(e) => handleChange('isPopular', e?.target?.checked)}
                className="w-5 h-5 rounded border-input text-primary focus:ring-2 focus:ring-primary cursor-pointer"
              />
              <label htmlFor="isPopular" className="flex-1 cursor-pointer">
                <span className="block text-sm font-caption font-medium text-foreground">
                  Mark as Popular
                </span>
                <span className="block text-xs text-muted-foreground font-caption mt-0.5">
                  Display a "Popular" badge on this {formData.type === 'product' ? 'product' : 'service'}
                </span>
              </label>
            </div>
          </div>
        </form>

        <div className="flex items-center justify-end gap-3 p-4 md:p-6 border-t border-border">
          <Button
            variant="outline"
            onClick={onClose}
            type="button"
          >
            Cancel
          </Button>
          <Button
            variant="default"
            onClick={handleSubmit}
            iconName="Save"
            iconPosition="left"
            iconSize={18}
          >
            {service
              ? `Update ${formData.type === 'product' ? 'Product' : 'Service'}`
              : `Add ${formData.type === 'product' ? 'Product' : formData.type === 'service' ? 'Service' : 'Item'}`}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ServiceModal;
